import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { Link } from 'react-router'

import RecordNoCommercialTermsButton from '@/components/misc/CommercialAgreementsDetails/components/RecordNoCommercialTermsButton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION } from '@/lib/constants'
import { faPlus } from '@/lib/fontAwesomePro/regular'
import { currentUrl } from '@/lib/helpers/routeHelpers'
import { ROUTES } from '@/lib/routes'
import { CommercialAgreementReferralDirection } from '@/types/misc'
import { PanelItem } from '@/types/responses/panels'

interface Props {
  panel: PanelItem
  referralDirection: CommercialAgreementReferralDirection
}

const AddAgreementSection: React.FC<Props> = ({ panel, referralDirection }) => {
  const currentCompany = useCurrentCompanyOrFail()
  const otherCompany =
    currentCompany.id === panel.fromCompany.id
      ? panel.toCompany
      : panel.fromCompany

  const isOutgoingAgreement =
    referralDirection === COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.OUTGOING

  const fromCompanyName = isOutgoingAgreement
    ? currentCompany.presentationName
    : otherCompany.presentationName

  const toCompanyName = isOutgoingAgreement
    ? otherCompany.presentationName
    : currentCompany.presentationName

  const agreementTemplateId = isOutgoingAgreement
    ? otherCompany.incomingReferralsAgreementTemplate?.id
    : otherCompany.outgoingReferralsAgreementTemplate?.id

  const shouldPromptUserToReviewAgreementTemplate = !!agreementTemplateId

  const noAgreementAddedMessage = (
    <>
      No commercial terms are currently in place for referrals from{' '}
      {fromCompanyName} to {toCompanyName}.
    </>
  )

  const currentCompanyHasAgreementTemplateForReferralDirection =
    referralDirection === COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.INCOMING
      ? !!currentCompany.incomingReferralsAgreementTemplate
      : !!currentCompany.outgoingReferralsAgreementTemplate

  if (shouldPromptUserToReviewAgreementTemplate) {
    return (
      <div>
        <p className="mb-3">{noAgreementAddedMessage}</p>

        <div className="flex flex-col sm:flex-row gap-2">
          <Link
            to={ROUTES.commercialAgreementTemplateReview({
              params: {
                commercialAgreementTemplateId: agreementTemplateId.toString(),
              },
            })}
          >
            <Button variant="primary" className="w-full sm:w-auto">
              Review and accept standard terms
            </Button>
          </Link>

          <RecordNoCommercialTermsButton
            agreementTemplateId={agreementTemplateId}
          />
        </div>
      </div>
    )
  }

  return (
    <section className="mb-4">
      <div>
        {currentCompanyHasAgreementTemplateForReferralDirection ? (
          <Alert variant="info">
            <AlertDescription>
              <p>
                Your firm has a commercial agreement template configured for{' '}
                referrals in this direction.
              </p>

              {isOutgoingAgreement ? (
                <p>
                  {otherCompany.presentationName} will be need to accept this
                  agreement template before you can send referrals to them.
                </p>
              ) : (
                <p>
                  {otherCompany.presentationName} will be prompted to review and
                  accept this agreement before they send a referral to your
                  firm.
                </p>
              )}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {noAgreementAddedMessage}
            <Link
              to={ROUTES.addRelationshipAgreement({
                params: { panelId: panel.id.toString() },
                searchParams: {
                  referralDirection,
                  returnPath: currentUrl(),
                },
              })}
              className="mt-2 block no-underline"
            >
              <Button
                variant="success"
                size="lg"
                className="flex w-full items-center justify-center gap-2
                  sm:w-auto sm:justify-start"
              >
                <FontAwesomeIcon icon={faPlus} />
                Add an agreement
              </Button>
            </Link>
          </>
        )}
      </div>
    </section>
  )
}

export default AddAgreementSection
